/* eslint-disable max-lines */
import {
    FaIcon,
    ServiceIcon, SkeletonMotion, ThemeTokens,
} from '@acp/core-ui';
import {
    EstimatedQuotePrices, Property,
    Quote,
    QuoteMapper,
} from '@acp/shared';
import {
    Badge,
    BorderProps,
    Button,
    ButtonGroup,
    Card,
    CardBody,
    CardBodyProps,
    Divider,
    Flex,
    GridItem,
    GridItemProps,
    HStack,
    Icon,
    IconButton,
    Image,
    Link,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    ResponsiveValue,
    Skeleton,
    Text,
    Tooltip,
    useDisclosure,
    VStack,
} from '@chakra-ui/react';
import {
    format, formatDistance, isBefore,
} from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { ForwardQuoteModal } from '../forward-quote-modal';
import { useQuotePdfDownload } from '../../hooks/use-quote-pdf-download';

const gap = 4;

const gridAreaProps: { [key: string]: GridItemProps } = {
    address: {
        gridArea: 'address',
        as: VStack,
        alignItems: 'start',
        justifyContent: 'flex-start',
        gap,
    },
    occupant: {
        gridArea: 'occupant',
    },
    reason: {
        gridArea: 'reason',
    },
    status: {
        gridArea: 'status',
    },
    actions: {
        gridArea: 'actions',
        as: HStack,
        alignSelf: 'flex-start',
        justifySelf: 'flex-end',
        gap,
    },
};

type AreaType = keyof typeof gridAreaProps;

interface RowColumnProps extends GridItemProps {
    area: AreaType;
}

export function QuoteRowLayout(
    {
        children,
        borderLeftColor = 'secondary.500',
    }:
    {
        children: React.ReactNode;
        borderLeftColor?: BorderProps['borderLeftColor'];
    }
) {
    return (
        <Card
            borderColor={ThemeTokens.Defaults.borderColor}
            borderWidth={1}
            borderLeftWidth={6}
            borderLeftColor={borderLeftColor}
            bg="white"
            variant="filled"
            w="full"
            shadow="sm"
            p={5}
        >
            {children}
        </Card>
    );
}

function RowBody({ children, as: Component, ...props }: CardBodyProps) {
    return (
        <CardBody as={Component} {...props}>
            {children}
        </CardBody>
    );
}

function RowColumn({ children, area, ...props }: RowColumnProps) {
    const gridItemAreaProps = gridAreaProps[area];

    return (
        <GridItem {...gridItemAreaProps} {...props}>
            {children}
        </GridItem>
    );
}

function RowIcon(
    { imgSrc, height }:
    { imgSrc: string; height?: ResponsiveValue<number | string> }
) {
    return (
        <Image src={imgSrc} h={height} />
    );
}

function PriceSkeleton() {
    return (
        <SkeletonMotion h={6}>
            <Flex direction="column">
                <Text fontSize="0.6em">
                    FROM
                </Text>
                <Text pb={1}>
                    9,999.99
                </Text>
            </Flex>
        </SkeletonMotion>
    );
}

function StatusSkeleton() {
    return (
        <SkeletonMotion>
            <Badge>
                Urgent Reason
            </Badge>
        </SkeletonMotion>
    );
}

function QuoteRowSkeleton(
    { gridWrapper }:
    { gridWrapper: React.ComponentType<{ children: React.ReactNode }> }
) {
    return (
        <Card
            borderWidth={1}
            borderColor="muted.50"
            bg="white"
            variant="filled"
            w="full"
            p={5}
        >
            <CardBody as={gridWrapper} p={0} alignItems="center" justifyItems="center">
                <GridItem as={VStack} gap={1} gridArea="quote-number" mb={[gap, null, null, 0]} alignItems="start">
                    <Skeleton>
                        <Text fontSize="small">Quote extra</Text>
                    </Skeleton>
                    <Skeleton>
                        <Text fontSize="x-small">
                            #12345678
                        </Text>
                    </Skeleton>
                </GridItem>
                <VStack
                    alignItems="start"
                    gap={1}
                    w={52}
                    gridArea="address"
                    mb={[gap, null, null, 0]}
                >
                    <SkeletonMotion height={5} />
                    <SkeletonMotion height={5} />
                </VStack>

                <GridItem
                    as={VStack}
                    gridArea="status"
                    area="status"
                    alignItems="start"
                    justifySelf={['start', 'end', null, 'start']}
                    gap={1}
                    pl={[0, 2, null, 0]}
                    mb={[gap, 0]}
                >
                    <StatusSkeleton />
                </GridItem>

                <GridItem area="price">
                    <PriceSkeleton />
                </GridItem>

                <GridItem as={HStack} gridArea="actions" justifyContent="end" width="100%" gap={3} alignSelf={['end', null, null, 'center']}>
                    <Skeleton>
                        <Button
                            borderRightWidth={1}
                            borderRightColor="secondary.900"
                            color="secondary.25"
                            size="sm"
                        >
                            actions menu xx
                        </Button>
                    </Skeleton>
                </GridItem>
            </CardBody>
        </Card>
    );
}

function QuoteNumber({ quoteNumber }: { quoteNumber: string }) {
    return (
        <VStack gap={0} alignItems="start">
            <Text fontWeight="medium">Quote</Text>
            <Text fontSize="small">
                {quoteNumber}
            </Text>
        </VStack>
    );
}

function Address({ property }: { property: Property }) {
    return (
        <Link
            as={RouterLink}
            to={`/property/${property.compliancePropertyId}`}
            textDecoration="none"
            _hover={{ textDecoration: 'underline' }}
            fontWeight="medium"
        >
            <Text>{property.addressLine1}</Text>
            <Text>{property.addressLine2}</Text>
        </Link>
    );
}

function QuoteStatus(
    { quote, data }:
    {
        quote: Quote;
        data: Quote & {
            estimatedPrices: EstimatedQuotePrices;
        }; }
) {
    const { expiresAt, service } = quote;

    const isExpired = isBefore(new Date(expiresAt), new Date());
    const expiryPrefix = isExpired ? 'Expired' : 'Expires';

    return (
        <HStack gap={3}>
            <Tooltip label={service.name} bgColor="white" color="black">
                <Icon w={6} h={6} color="primary.500">
                    <ServiceIcon service={service.shortName} />
                </Icon>
            </Tooltip>

            <Divider orientation="vertical" h={6} borderColor="muted.200" />

            <VStack direction="column" align="left" gap={1}>
                <Badge colorScheme="danger" fontSize="sm" w="fit-content" variant="subtle" cursor="default">
                    {QuoteMapper.getQuoteSeverity(data.quoteSeverity)}
                </Badge>
                {
                    expiresAt !== null && (
                        <Tooltip label={`${expiryPrefix} on ${format(expiresAt, 'dd/MM/yyyy')}`} bgColor="white" color="black">
                            <Text color={isExpired ? 'danger.600' : 'muted.500'} fontSize="xs" fontWeight="bold" cursor="default">
                                {`${expiryPrefix} ${formatDistance(expiresAt, new Date(), { addSuffix: true }) ?? 'No expiry date'}`}
                            </Text>
                        </Tooltip>
                    )
                }
            </VStack>
        </HStack>
    );
}

function QuotePrice({ data }: { data: Quote & {
    estimatedPrices: EstimatedQuotePrices;
}; }) {
    return (
        <VStack align="left" spacing={0}>
            <Text fontSize="0.6em">
                FROM
            </Text>
            <Text>
                {`$${data.estimatedPrices.minimum?.toLocaleString()}`}
            </Text>
        </VStack>
    );
}

function QuoteRowActions({ quote }: { quote: Quote }) {
    const downloadMutation = useQuotePdfDownload();
    const forwardQuoteDisclosure = useDisclosure();

    const {
        complianceQuoteId,
        property,
        approvalUrl,
    } = quote;

    return (
        <>
            <ButtonGroup size="sm" isAttached colorScheme="charcoal" variant="outline" justifySelf="end">
                {
                    approvalUrl !== null && (
                        <Button
                            as={RouterLink}
                            to={approvalUrl}
                            target="_blank"
                            borderRightRadius={0}
                            borderRight="none"
                            borderColor={ThemeTokens.Defaults.borderColor}
                            color="muted.700"
                        >
                            Review quote
                        </Button>
                    )
                }
                <Menu size="large">
                    <MenuButton
                        as={IconButton}
                        borderStartRadius={0}
                        aria-label="Add to friends"
                        icon={<FaIcon icon={faChevronDown} />}
                        borderColor={ThemeTokens.Defaults.borderColor}
                        color="muted.700"
                    />
                    <MenuList>
                        <MenuItem onClick={forwardQuoteDisclosure.onOpen}>
                            Send quote
                        </MenuItem>
                        <MenuItem
                            onClick={() => downloadMutation.download({ quote })}
                        >
                            Download PDF
                        </MenuItem>
                    </MenuList>
                </Menu>
            </ButtonGroup>

            <ForwardQuoteModal
                isOpen={forwardQuoteDisclosure.isOpen}
                onClose={forwardQuoteDisclosure.onClose}
                quoteId={complianceQuoteId}
                propertyId={property.compliancePropertyId}
            />
        </>

    );
}

QuoteRowLayout.Body = RowBody;
QuoteRowLayout.Column = RowColumn;
QuoteRowLayout.Icon = RowIcon;
QuoteRowLayout.Skeleton = QuoteRowSkeleton;
QuoteRowLayout.PriceSkeleton = PriceSkeleton;
QuoteRowLayout.StatusSkeleton = StatusSkeleton;
QuoteRowLayout.QuoteNumber = QuoteNumber;
QuoteRowLayout.Address = Address;
QuoteRowLayout.Status = QuoteStatus;
QuoteRowLayout.Price = QuotePrice;
QuoteRowLayout.Actions = QuoteRowActions;
